import { useAccount } from 'wagmi';
import { Box, Link, Tooltip, ColumnDef, Flex, SkeletonSquare, Image } from '@node-real/uikit';
import { useEffect, useState, useMemo, useRef } from 'react';
import styled from '@emotion/styled';
import BN from 'bn.js';
import { useRouter } from 'next/router';
import {
  HistoryTable,
  mobileMedia,
  usePagination,
  useResponsive,
  useDepositHistoryPageKey,
} from '@op-bridge/bridge-core';

import { TableWrapper } from '../TableWrapper';
import { trimLongStr, divide10Exp, formatNum, formatLocalTime, getUTCZone } from '../../utils';
import { BACKEND_DOMAIN, BSC_SCAN, NET_ENV } from '../../env';
import { useGetTokenList } from '../../hooks/useGetTokenList';
import { menus } from '../Menu';
const FakeHistoryList = Array(9).fill({
  blockTimeStamp: 1682561592,
  l1TxHash: '...',
  l1ProveTxHash: '0x0000000000000000000000000000000000000000000000000000000000000000',
  gasPrice: 1500000008,
  gasUsed: 120187,
  hash: '0x0000000000000000000000000000000000000000000000000000000000000000',
  receiptsStatus: 0,
  value: '0x0',
});

const getTransactionStatus = (txn: any): string => {
  if (!txn.blockTimeStamp) {
    return 'Pending';
  }
  const status = txn?.receiptsStatus === 1 ? 'Completed' : 'Failed';
  return status;
};
const cellheight = 56;
const pageSize = 20;
const loadDataSize = 500; // page size in api call

export const DepositHistory = () => {
  const { address: wagmiAddress, isDisconnected } = useAccount();
  const { page, handlePageChange } = usePagination();
  // state
  const [address, setAddress] = useState<string | null>(null);
  const router = useRouter() as any;
  const ref = useRef(true);
  const [isInitLoading, setInitIsLoading] = useState(true);
  const { tokensList, isLoading } = useGetTokenList();

  const { data: historyPage1, isFetching } = useDepositHistoryPageKey(
    address,
    '',
    loadDataSize,
    true,
    'p1',
  );

  const { data: historyPage2, isFetching: isFetching2 } = useDepositHistoryPageKey(
    address,
    historyPage1?.pageKey,
    loadDataSize,
    !!historyPage1?.pageKey,
    'p2',
  );

  const { data: historyPage3, isFetching: isFetching3 } = useDepositHistoryPageKey(
    address,
    historyPage2?.pageKey,
    loadDataSize,
    !!historyPage2?.pageKey,
    'p3',
  );

  const { data: historyPage4, isFetching: isFetching4 } = useDepositHistoryPageKey(
    address,
    historyPage3?.pageKey,
    loadDataSize,
    !!historyPage3?.pageKey,
    'p4',
  );

  const { isMobile } = useResponsive();

  useEffect(() => {
    setTimeout(() => (ref.current = false), 2000);
    setTimeout(() => setInitIsLoading(false), 100);
    return () => {
      ref.current = true;
      setInitIsLoading(true);
    };
  }, []);

  const wholeHistoryList = useMemo(() => {
    if (
      historyPage2?.list.length > 0 &&
      historyPage3?.list.length > 0 &&
      historyPage4?.list.length > 0
    ) {
      return [
        ...historyPage1?.list,
        ...historyPage2?.list,
        ...historyPage3?.list,
        ...historyPage4?.list,
      ];
    } else if (historyPage2?.list.length > 0 && historyPage3?.list.length > 0) {
      return [...historyPage1?.list, ...historyPage2?.list, ...historyPage3?.list];
    } else if (historyPage2?.list.length > 0) {
      return [...historyPage1?.list, ...historyPage2?.list];
    } else if (historyPage1?.list.length > 0) {
      return [...historyPage1?.list];
    } else {
      return [];
    }
  }, [historyPage1?.list, historyPage2?.list, historyPage3?.list, historyPage4?.list]);

  const timeColumn = useMemo(() => {
    return {
      accessorKey: 'time',
      header: 'Time',
      size: 245,
      cell: (info: any) => {
        const {
          row: { original: data },
        } = info;
        return (
          <DateTime>
            {formatLocalTime(data.blockTimeStamp * 1000, 'YYYY-M-DD HH:mm')} (UTC
            {getUTCZone(data.blockTimeStamp * 1000)})
          </DateTime>
        );
      },
    };
  }, []);

  const amountColumn = useMemo(() => {
    return {
      accessorKey: 'amount',
      header: 'Amount',
      size: 205,
      cell: (info: any) => {
        const {
          row: { original: data },
        } = info;
        let valStr = '';
        let tokenAsset = null;
        let icon = null;
        let assetName = data?.asset;
        try {
          tokenAsset = tokensList.filter((asset) => {
            return asset.symbol === assetName;
          })[0];
          icon = menus.filter((asset) => asset.name === assetName)[0]?.icon;
          if (data?.asset === 'BNB') {
            /**
             * BNB amount
             */
            if (data.value.includes('0x')) {
              const valBn = new BN(data.value.slice(2), 16);
              valStr = Number(divide10Exp(valBn, 18)).toLocaleString('fullwide', {
                maximumFractionDigits: 18,
              });
            } else {
              valStr = (Number(data.value) * Math.pow(10, -18)).toLocaleString('fullwide', {
                maximumFractionDigits: 18,
              });
            }
          } else {
            /**
             * Bep20 Token Amount
             */
            if (data?.amount !== null || data?.amount !== undefined) {
              const BNvalue = new BN(data?.amount.replace('0x', ''), 16);
              const amount = Number(divide10Exp(BNvalue, 18));
              valStr = Number(amount).toLocaleString('fullwide', {
                maximumFractionDigits: 18,
              });
            }
          }
        } catch (e) {
          //eslint-disable-next-line no-console
          console.log(e);
        }
        if (valStr?.length > 15) {
          return (
            <Tooltip placement="bottom-start" content={valStr}>
              <Flex
                flexDirection={'row'}
                alignItems={'center'}
                flexWrap={'nowrap'}
                fontSize={[14, 12]}
                fontWeight={500}
              >
                {tokenAsset && tokenAsset?.logoURI ? (
                  <Image
                    width={20}
                    height={20}
                    mr={4}
                    alt={tokenAsset.symbol}
                    src={tokenAsset.logoURI}
                    borderRadius={'50%'}
                  />
                ) : (
                  <>
                    <Box mr={4}>{icon}</Box>
                  </>
                )}{' '}
                {`${trimLongStr(valStr, 15, 15, 0)} ${assetName}`}
              </Flex>
            </Tooltip>
          );
        } else {
          return (
            <Flex
              flexDirection={'row'}
              alignItems={'center'}
              flexWrap={'nowrap'}
              fontSize={[14, 12]}
              fontWeight={500}
            >
              {tokenAsset && tokenAsset.logoURI ? (
                <Image
                  width={20}
                  height={20}
                  mr={4}
                  alt={tokenAsset.symbol}
                  src={tokenAsset.logoURI}
                  borderRadius={'50%'}
                />
              ) : (
                <>{icon && <Box mr={4}>{icon}</Box>}</>
              )}{' '}
              {`${valStr} ${assetName}`}
            </Flex>
          );
        }
      },
    };
  }, [tokensList]);

  const depositTxColumns: ColumnDef<any> = useMemo(() => {
    return [
      isMobile ? amountColumn : timeColumn,
      isMobile ? timeColumn : amountColumn,
      {
        accessorKey: 'deposit_tx',
        header: 'Transaction',
        size: 348,
        cell: (info: any) => {
          const {
            row: { original: data },
          } = info;
          return (
            <div>
              <Link
                href={`${BSC_SCAN[NET_ENV]}/tx/${data?.l1TxHash}`}
                target="_blank"
                fontWeight={500}
                color={'readable.link'}
                _hover={{ color: 'scene.primary.normal' }}
              >
                {isMobile
                  ? trimLongStr(data?.l1TxHash, 20, 10, 10)
                  : trimLongStr(data?.l1TxHash, 28, 15, 13)}
              </Link>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: <StatusHeader>Status</StatusHeader>,
        size: 153,
        cell: (info: any) => {
          const {
            row: { original: data },
          } = info;
          const status = getTransactionStatus(data);
          return <Status type={status}>{status}</Status>;
        },
      },
    ];
  }, [isMobile, amountColumn, timeColumn]);

  useEffect(() => {
    if (wagmiAddress) {
      setAddress(wagmiAddress);
      handlePageChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wagmiAddress]);

  useEffect(() => {
    if (!wagmiAddress || isDisconnected) {
      if (window) {
        window.location.href = '/';
      }
    }
  }, [wagmiAddress, router, isDisconnected]);

  if (isDisconnected) {
    return <div></div>;
  }

  const isLoadingColumns = depositTxColumns.map((column) => ({
    ...column,
    cell: () => <SkeletonSquare style={{ width: '80%' }} />,
  }));

  const finalColumns =
    (isFetching || isFetching2 || isFetching3 || isFetching4 || isLoading || isInitLoading) &&
    ref.current &&
    !isMobile
      ? isLoadingColumns
      : depositTxColumns;

  return (
    <Container>
      <Box>
        <Box>
          <HistoryTable
            type="deposit"
            data={
              (isFetching ||
                isFetching2 ||
                isFetching3 ||
                isFetching4 ||
                isLoading ||
                isInitLoading) &&
              ref.current
                ? isMobile
                  ? wholeHistoryList?.slice((page - 1) * pageSize, page * pageSize - 1)
                  : FakeHistoryList
                : wholeHistoryList?.slice((page - 1) * pageSize, page * pageSize - 1) || []
            }
            withdrawTxColumns={finalColumns}
            isLoading={
              isFetching || isFetching2 || isFetching3 || isFetching4 || isLoading || isInitLoading
            }
            pageSize={pageSize}
            history={{
              list: wholeHistoryList?.slice((page - 1) * pageSize, page * pageSize - 1) || [],
              total: historyPage1?.total_num || 0,
            }}
            cellHeight={cellheight}
            emptyText={'No Deposit History.'}
            topContent={
              <TableWrapper
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                text={`Total of ${formatNum(historyPage1?.total_num || 0)} deposit transactions`}
                total={historyPage1?.total_num || 0}
              />
            }
            bottomContent={
              <TableWrapper
                page={page}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                text={`Total of ${formatNum(historyPage1?.total_num || 0)} deposit transactions`}
                total={historyPage1?.total_num || 0}
              />
            }
          />
        </Box>
      </Box>
    </Container>
  );
};

const Container = styled.div`
  max-width: 1181px;
  width: 100%;
  padding: 16px 24px 0;
  margin: 0 auto;
  ${mobileMedia} {
    padding: 0 12px;
  }
`;

const StatusHeader = styled(Flex)`
  cursor: pointer;
  align-items: center;
  &:hover svg {
    color: ${(props: any) => props.theme.colors.scene.primary.active};
  }
`;

// const TooltipText = styled.div`
//   font-weight: 400;
//   font-size: 14px;
//   line-height: 20px;
// `;

const DateTime = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  ${mobileMedia} {
    font-size: 12px;
  }
`;

const Status = styled.div<{ type: string }>`
  padding: 4.5px 8px;
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  border-radius: 12px;
  background: ${(props: any) =>
    props.type === 'Pending'
      ? props.theme.colors.scene.warning.opacity
      : props.type === 'Failed'
      ? props.theme.colors.scene.danger.opacity
      : props.theme.colors.bg?.status};
  color: ${(props: any) =>
    props.type === 'Pending'
      ? props.theme.colors.scene.warning.normal
      : props.type === 'Failed'
      ? props.theme.colors.scene?.danger.normal
      : props.theme.colors.readable?.top.secondary};
  width: 100%;
  width: fit-content;
`;
